import { lazy, Suspense, type ComponentType } from "react";
import { Navigate, Routes, Route } from "react-router-dom";
import { RotaProtegida } from "./router/RotaProtegida";
import { RotaDonoPlataforma } from "./router/RotaDonoPlataforma";
import { LayoutPrincipal } from "./layout/LayoutPrincipal";
import { RecursoEmpresaRoute } from "./router/RecursoEmpresaRoute";
import { GlobalTooltip } from "./components/ui/GlobalTooltip";

/** Cada página vira um chunk próprio e só é baixada quando a rota é acessada. */
function lazyNamed(
  carregar: () => Promise<unknown>,
  nome: string,
) {
  return lazy(async () => {
    const modulo = await carregar() as Record<string, ComponentType<any>>;
    return { default: modulo[nome] };
  });
}

function CarregandoRota() {
  return (
    <div role="status" aria-live="polite" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      Carregando...
    </div>
  );
}

const LoginPage = lazyNamed(() => import("./pages/Login/LoginPage"), "LoginPage");
const paginasAuth = () => import("./pages/Login/PaginasAuth");
const RecuperarSenhaPage = lazyNamed(paginasAuth, "RecuperarSenhaPage");
const CadastroPage = lazyNamed(paginasAuth, "CadastroPage");
const TermosPage = lazyNamed(paginasAuth, "TermosPage");
const PoliticaPage = lazyNamed(paginasAuth, "PoliticaPage");
const PoliticaCookiesPage = lazyNamed(paginasAuth, "PoliticaCookiesPage");
const PoliticaCancelamentoPage = lazyNamed(paginasAuth, "PoliticaCancelamentoPage");
const CadastroEmpresaPage = lazyNamed(() => import("./pages/CadastroEmpresa/CadastroEmpresaPage"), "CadastroEmpresaPage");
const ClientesPage = lazyNamed(() => import("./pages/CadastroCliente/ClientesPage"), "ClientesPage");
const FornecedoresPage = lazyNamed(() => import("./pages/CadFornecedor/FornecedoresPage"), "FornecedoresPage");
const TransportadorasPage = lazyNamed(() => import("./pages/CadTransportadora/TransportadorasPage"), "TransportadorasPage");
const ColaboradoresPage = lazyNamed(() => import("./pages/CadastroColaborador/ColaboradoresPage"), "ColaboradoresPage");
const ProdutosPage = lazyNamed(() => import("./pages/CadProduto/ProdutosPage"), "ProdutosPage");
const DetalhesPage = lazyNamed(() => import("./pages/CadProduto/DetalhesPage"), "DetalhesPage");
const FaixasPrecoPage = lazyNamed(() => import("./pages/CadDiversos/FaixasPrecoPage"), "FaixasPrecoPage");
const TabelaDetalhesPage = lazyNamed(() => import("./pages/CadDiversos/TabelaDetalhesPage"), "TabelaDetalhesPage");
const ServicosPage = lazyNamed(() => import("./pages/CadServico/ServicosPage"), "ServicosPage");
const UnidadesPage = lazyNamed(() => import("./pages/CadEstoque/UnidadesPage"), "UnidadesPage");
const CategoriasPage = lazyNamed(() => import("./pages/CadEstoque/CategoriasPage"), "CategoriasPage");
const DetalhesFichaPage = lazyNamed(() => import("./pages/CadEstoque/DetalhesFichaPage"), "DetalhesFichaPage");
const LocaisPage = lazyNamed(() => import("./pages/CadEstoque/LocaisPage"), "LocaisPage");
const TamanhosPage = lazyNamed(() => import("./pages/CadDiversos/TamanhosPage"), "TamanhosPage");
const CoresPage = lazyNamed(() => import("./pages/CadDiversos/CoresPage"), "CoresPage");
const EtiquetasPage = lazyNamed(() => import("./pages/CadDiversos/EtiquetasPage"), "EtiquetasPage");
const RespostasRapidasPage = lazyNamed(() => import("./pages/CadDiversos/RespostasRapidasPage"), "RespostasRapidasPage");
const ErrosPage = lazyNamed(() => import("./pages/CadDiversos/ErrosPage"), "ErrosPage");
const SeriaisPage = lazyNamed(() => import("./pages/CadDiversos/SeriaisPage"), "SeriaisPage");
const BancosPage = lazyNamed(() => import("./pages/CadBanco/BancosPage"), "BancosPage");
const PerfisPage = lazyNamed(() => import("./pages/CadPerfil/PerfisPage"), "PerfisPage");
const LoginsPage = lazyNamed(() => import("./pages/CadLogin/LoginsPage"), "LoginsPage");
const LogAcessoPage = lazyNamed(() => import("./pages/Auditoria/LogAcessoPage"), "LogAcessoPage");
const ChatInternoAdminPage = lazyNamed(() => import("./pages/Auditoria/ChatInternoPage"), "ChatInternoAdminPage");
const RotaInicial = lazyNamed(() => import("./pages/VisaoGeral/RotaInicial"), "RotaInicial");
const ConfiguracoesPage = lazyNamed(() => import("./pages/Configuracoes/ConfiguracoesPage"), "ConfiguracoesPage");
const EmConstrucao = lazyNamed(() => import("./pages/EmConstrucao"), "EmConstrucao");
const BlocoNotasPage = lazyNamed(() => import("./pages/BlocoNotas/BlocoNotasPage"), "BlocoNotasPage");
const ProtocoloPage = lazyNamed(() => import("./pages/Plataforma/ProtocoloPage"), "ProtocoloPage");
const VendasPage = lazyNamed(() => import("./pages/Vendas/VendasPage"), "VendasPage");
const FichaTecnicaFardamentoPage = lazyNamed(() => import("./pages/Vendas/FichaTecnicaFardamentoPage"), "FichaTecnicaFardamentoPage");
const PedidosPage = lazyNamed(() => import("./pages/Vendas/PedidosPage"), "PedidosPage");
const EntregasPage = lazyNamed(() => import("./pages/Vendas/EntregasPage"), "EntregasPage");
const CobrancasPage = lazyNamed(() => import("./pages/Vendas/CobrancasPage"), "CobrancasPage");
const CobrancasRevendaPage = lazyNamed(() => import("./pages/Compras/CobrancasRevendaPage"), "CobrancasRevendaPage");
const AtivacoesPage = lazyNamed(() => import("./pages/Suporte/AtivacoesPage"), "AtivacoesPage");
const ComprasPage = lazyNamed(() => import("./pages/Compras/ComprasPage"), "ComprasPage");
const GanttComprasPage = lazyNamed(() => import("./pages/Compras/GanttComprasPage"), "GanttComprasPage");
const SaldoComprasPage = lazyNamed(() => import("./pages/Compras/SaldoComprasPage"), "SaldoComprasPage");
const FornecedoresPedidosPage = lazyNamed(() => import("./pages/Compras/FornecedoresPedidosPage"), "FornecedoresPedidosPage");
const OrdemEntregaPage = lazyNamed(() => import("./pages/Compras/OrdemEntregaPage"), "OrdemEntregaPage");
const EnvioNotaFiscalRevendaPage = lazyNamed(() => import("./pages/Compras/RevendaPaginasVazias"), "EnvioNotaFiscalRevendaPage");
const DistribuicaoRevendaPage = lazyNamed(() => import("./pages/Compras/RevendaPaginasVazias"), "DistribuicaoRevendaPage");
const ComprovantesPagamentoFornecedorPage = lazyNamed(() => import("./pages/Compras/ComprovantesPagamentoFornecedorPage"), "ComprovantesPagamentoFornecedorPage");
const MonitorFiscalPage = lazyNamed(() => import("./pages/Vendas/MonitorFiscalPage"), "MonitorFiscalPage");
const NfesRecebidasPage = lazyNamed(() => import("./pages/Vendas/NfesRecebidasPage"), "NfesRecebidasPage");
const SuportePage = lazyNamed(() => import("./pages/Suporte/SuportePage"), "SuportePage");
const KanbanSuportePage = lazyNamed(() => import("./pages/Suporte/KanbanSuportePage"), "KanbanSuportePage");
const CalendarioSuportePage = lazyNamed(() => import("./pages/Suporte/CalendarioSuportePage"), "CalendarioSuportePage");
const ChatSuportePage = lazyNamed(() => import("./pages/Suporte/ChatSuportePage"), "ChatSuportePage");
const AvaliacoesAtendimentoPage = lazyNamed(() => import("./pages/Suporte/AvaliacoesAtendimentoPage"), "AvaliacoesAtendimentoPage");
const AvaliarAtendimentoPublicoPage = lazyNamed(() => import("./pages/Suporte/AvaliarAtendimentoPublicoPage"), "AvaliarAtendimentoPublicoPage");
const EtapasPage = lazyNamed(() => import("./pages/Suporte/EtapasPage"), "EtapasPage");
const OficinasPage = lazyNamed(() => import("./pages/CadDiversos/OficinasPage"), "OficinasPage");
const FormatacaoPage = lazyNamed(() => import("./pages/Producao/FormatacaoPage"), "FormatacaoPage");
const ListaProducaoPage = lazyNamed(() => import("./pages/Producao/ListaProducaoPage"), "ListaProducaoPage");
const KanbanProducaoPage = lazyNamed(() => import("./pages/Producao/KanbanProducaoPage"), "KanbanProducaoPage");
const CalendarioProducaoPage = lazyNamed(() => import("./pages/Producao/CalendarioProducaoPage"), "CalendarioProducaoPage");
const GanttProducaoPage = lazyNamed(() => import("./pages/Producao/GanttProducaoPage"), "GanttProducaoPage");
const AvariasProducaoPage = lazyNamed(() => import("./pages/Producao/AvariasProducaoPage"), "AvariasProducaoPage");
const ComissoesProducaoPage = lazyNamed(() => import("./pages/Producao/ComissoesProducaoPage"), "ComissoesProducaoPage");
const MetasPage = lazyNamed(() => import("./pages/CadMetas/MetasPage"), "MetasPage");
const ProducaoEstoquePage = lazyNamed(() => import("./pages/Producao/ProducaoEstoquePage"), "ProducaoEstoquePage");
const ImpressoesDtfPage = lazyNamed(() => import("./pages/Producao/ImpressoesDtfPage"), "ImpressoesDtfPage");
const ControleProducaoPage = lazyNamed(() => import("./pages/Producao/ControleProducaoPage"), "ControleProducaoPage");
const GestaoOficinasPage = lazyNamed(() => import("./pages/Producao/GestaoOficinasPage"), "GestaoOficinasPage");
const ExpedicaoProducaoPage = lazyNamed(() => import("./pages/Producao/ExpedicaoProducaoPage"), "ExpedicaoProducaoPage");
const ChecklistsPage = lazyNamed(() => import("./pages/Suporte/ChecklistsPage"), "ChecklistsPage");
const SuporteExternoPage = lazyNamed(() => import("./pages/Suporte/SuporteExternoPage"), "SuporteExternoPage");
const OrcamentosSuportePage = lazyNamed(() => import("./pages/Suporte/OrcamentosSuportePage"), "OrcamentosSuportePage");
const ChecklistCadastroPage = lazyNamed(() => import("./pages/Suporte/ChecklistCadastroPage"), "ChecklistCadastroPage");
const ReceitasPage = lazyNamed(() => import("./pages/Financeiro/ReceitasPage"), "ReceitasPage");
const DespesasPage = lazyNamed(() => import("./pages/Financeiro/DespesasPage"), "DespesasPage");
const ComissoesFinanceiroPage = lazyNamed(() => import("./pages/Financeiro/ComissoesFinanceiroPage"), "ComissoesFinanceiroPage");
const CaixaPage = lazyNamed(() => import("./pages/Financeiro/CaixaPage"), "CaixaPage");
const DrePage = lazyNamed(() => import("./pages/Financeiro/DrePage"), "DrePage");
const PrevisibilidadePage = lazyNamed(() => import("./pages/Financeiro/PrevisibilidadePage"), "PrevisibilidadePage");
const VendasParceladasPage = lazyNamed(() => import("./pages/Financeiro/VendasParceladasPage"), "VendasParceladasPage");
const PlanoContasPage = lazyNamed(() => import("./pages/Financeiro/PlanoContasPage"), "PlanoContasPage");
const MaquininhasPage = lazyNamed(() => import("./pages/Financeiro/MaquininhasPage"), "MaquininhasPage");
const InventarioAjusteEstoquePage = lazyNamed(() => import("./pages/Financeiro/InventarioAjusteEstoquePage"), "InventarioAjusteEstoquePage");
const EstoquePage = lazyNamed(() => import("./pages/Estoque/EstoquePage"), "EstoquePage");
const TransferenciasEstoquePage = lazyNamed(() => import("./pages/Estoque/TransferenciasEstoquePage"), "TransferenciasEstoquePage");
const RelatoriosHubPage = lazyNamed(() => import("./pages/Relatorios/RelatoriosHubPage"), "RelatoriosHubPage");
const RotaNaoEncontrada = lazyNamed(() => import("./router/RotaNaoEncontrada"), "RotaNaoEncontrada");

export function App() {
  return (
    <>
      <GlobalTooltip />
      <Suspense fallback={<CarregandoRota />}>
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/recuperar-senha" element={<RecuperarSenhaPage />} />
        <Route path="/redefinir-senha/:token" element={<RecuperarSenhaPage />} />
        <Route path="/cadastro" element={<CadastroPage />} />
        <Route path="/termos" element={<TermosPage />} />
        <Route path="/politica" element={<PoliticaPage />} />
        <Route path="/cookies" element={<PoliticaCookiesPage />} />
        <Route path="/cancelamento" element={<PoliticaCancelamentoPage />} />
        <Route path="/avaliar-atendimento/:token" element={<AvaliarAtendimentoPublicoPage />} />
        <Route
          element={
            <RotaProtegida>
              <LayoutPrincipal />
            </RotaProtegida>
          }
        >
          <Route index element={<RotaInicial />} />

          <Route path="cadastros/empresas" element={<CadastroEmpresaPage />} />
          <Route path="cadastros/bancos" element={<BancosPage />} />
          <Route path="cadastros/clientes" element={<ClientesPage />} />
          <Route path="cadastros/fornecedores" element={<FornecedoresPage />} />
          <Route path="cadastros/transportadoras" element={<TransportadorasPage />} />
          <Route path="cadastros/colaboradores" element={<ColaboradoresPage />} />
          <Route path="cadastros/produtos" element={<ProdutosPage />} />
          <Route path="cadastros/detalhes" element={<DetalhesPage />} />
          <Route path="cadastros/faixas-preco" element={<FaixasPrecoPage />} />
          <Route path="cadastros/tabela-detalhes" element={<TabelaDetalhesPage />} />
          <Route path="cadastros/servicos" element={<ServicosPage />} />
          <Route path="cadastros/unidades-medida" element={<UnidadesPage />} />
          <Route path="cadastros/categorias-estoque" element={<CategoriasPage />} />
          <Route path="cadastros/detalhes-ficha" element={<DetalhesFichaPage />} />
          <Route path="cadastros/etapas" element={<EtapasPage />} />
          <Route path="cadastros/oficinas" element={<OficinasPage />} />
          <Route path="cadastros/locais-estoque" element={<LocaisPage />} />
          <Route path="cadastros/tamanhos" element={<TamanhosPage />} />
          <Route path="cadastros/cores" element={<CoresPage />} />
          <Route path="cadastros/etiquetas" element={<EtiquetasPage />} />
          <Route path="cadastros/respostas-rapidas" element={<RespostasRapidasPage />} />
          <Route path="cadastros/erros" element={<ErrosPage />} />
          <Route path="cadastros/seriais" element={<SeriaisPage />} />
          <Route path="cadastros/metas" element={<MetasPage />} />

          <Route path="vendas/visao-geral" element={<Navigate to="/vendas" replace />} />
          <Route path="vendas" element={<VendasPage />} />
          <Route path="vendas/ficha-tecnica" element={<FichaTecnicaFardamentoPage />} />
          <Route path="vendas/pedidos" element={<Navigate to="/area-cliente/pedidos" replace />} />
          <Route path="vendas/entregas" element={<EntregasPage />} />
          <Route path="vendas/cobrancas" element={<CobrancasPage />} />
          <Route path="vendas/atendimento" element={<ChatSuportePage tipoAtendimento="VENDAS" />} />
          <Route path="vendas/avaliacoes-atendimento" element={<AvaliacoesAtendimentoPage tipoAtendimento="VENDAS" />} />
          <Route path="vendas/monitor-fiscal" element={<RecursoEmpresaRoute recurso="fiscal"><MonitorFiscalPage /></RecursoEmpresaRoute>} />
          <Route path="vendas/nfes-recebidas" element={<RecursoEmpresaRoute recurso="fiscal"><NfesRecebidasPage /></RecursoEmpresaRoute>} />
          <Route path="compras/visao-geral" element={<Navigate to="/compras" replace />} />
          <Route path="compras" element={<ComprasPage />} />
          <Route path="compras/saldo-compras" element={<SaldoComprasPage />} />
          <Route path="compras/comprovantes-pagamentos" element={<ComprovantesPagamentoFornecedorPage key="compras-enviados" enviados />} />
          <Route path="compras/gantt" element={<GanttComprasPage />} />
          <Route path="compras/fornecedores" element={<Navigate to="/fornecedor/pedidos" replace />} />
          <Route path="compras/ordem-entrega" element={<Navigate to="/fornecedor/entregas" replace />} />
          <Route path="compras/relatorios" element={<RelatoriosHubPage modulo="compras" />} />
          <Route path="fornecedor/visao-geral" element={<FornecedoresPedidosPage dashboard />} />
          <Route path="fornecedor/pedidos" element={<FornecedoresPedidosPage />} />
          <Route path="fornecedor/cobrancas" element={<CobrancasRevendaPage />} />
          <Route path="fornecedor/saldo-interno" element={<SaldoComprasPage revenda />} />
          <Route path="fornecedor/entregas" element={<OrdemEntregaPage />} />
          <Route path="fornecedor/envio-nota-fiscal" element={<EnvioNotaFiscalRevendaPage />} />
          <Route path="fornecedor/distribuicao" element={<DistribuicaoRevendaPage />} />
          <Route path="fornecedor/comprovantes-pagamentos" element={<ComprovantesPagamentoFornecedorPage />} />
          <Route path="fornecedor/relatorios" element={<RelatoriosHubPage modulo="fornecedor" />} />
          <Route path="vendas/compras" element={<ComprasPage />} />

          <Route path="suporte/visao-geral" element={<Navigate to="/suporte" replace />} />
          <Route path="suporte/ativacoes" element={<AtivacoesPage />} />
          <Route path="suporte/kanban" element={<KanbanSuportePage />} />
          <Route path="suporte/calendario" element={<CalendarioSuportePage />} />
          <Route path="suporte/chat" element={<ChatSuportePage />} />
          <Route path="suporte/avaliacoes-atendimento" element={<AvaliacoesAtendimentoPage tipoAtendimento="SUPORTE" />} />
          <Route path="suporte/checklists" element={<ChecklistsPage modo="execucao" />} />
          <Route path="suporte/externo" element={<SuporteExternoPage />} />
          <Route path="suporte/orcamentos" element={<OrcamentosSuportePage />} />
          <Route path="cadastros/checklists" element={<ChecklistsPage modo="cadastro" />} />
          <Route path="cadastros/checklists/novo" element={<ChecklistCadastroPage />} />
          <Route path="cadastros/checklists/:id" element={<ChecklistCadastroPage />} />
          <Route path="suporte/agente-ia" element={<Navigate to="/configuracoes" replace />} />
          <Route path="suporte/base-conhecimento" element={<Navigate to="/configuracoes" replace />} />
          <Route path="suporte" element={<SuportePage />} />
          <Route path="producao/visao-geral" element={<Navigate to="/producao/lista" replace />} />
          <Route path="producao/vendas-dtf" element={<ChatSuportePage tipoAtendimento="PRODUCAO" />} />
          <Route path="producao/comprovantes-dtf" element={<ComprovantesPagamentoFornecedorPage key="dtf" dtf />} />
          <Route path="producao/avaliacoes-atendimento" element={<AvaliacoesAtendimentoPage tipoAtendimento="PRODUCAO" />} />
          <Route path="producao/formatacao" element={<FormatacaoPage />} />
          <Route path="producao/lista" element={<ListaProducaoPage />} />
          <Route path="producao/controle" element={<ControleProducaoPage />} />
          <Route path="producao/kanban" element={<KanbanProducaoPage />} />
          <Route path="producao/calendario" element={<CalendarioProducaoPage />} />
          <Route path="producao/gantt" element={<GanttProducaoPage />} />
          <Route path="producao/oficinas" element={<GestaoOficinasPage />} />
          <Route path="producao/estoque" element={<ProducaoEstoquePage />} />
          <Route path="producao/impressoes-dtf" element={<ImpressoesDtfPage />} />
          <Route path="producao/avarias" element={<AvariasProducaoPage />} />
          <Route path="producao/comissoes" element={<ComissoesProducaoPage />} />
          <Route path="producao/expedicao" element={<ExpedicaoProducaoPage />} />
          <Route path="producao/checklists" element={<ChecklistsPage modo="execucao" contexto="producao" />} />
          <Route path="producao/relatorios" element={<RelatoriosHubPage modulo="producao" />} />
          <Route path="suporte/relatorios" element={<RelatoriosHubPage modulo="suporte" />} />
          <Route path="area-cliente/pedidos" element={<PedidosPage />} />
          <Route path="area-cliente/chamados" element={<SuportePage modoAreaCliente />} />
          {/* O submenu Chat abre o painel flutuante no layout; não possui tela própria. */}
          <Route path="area-cliente/chat" element={<Navigate to="/" replace />} />
          <Route path="area-cliente/relatorios" element={<RelatoriosHubPage modulo="areaCliente" />} />

          <Route path="estoque" element={<Navigate to="/estoque/saldo" replace />} />
          <Route path="estoque/visao-geral" element={<Navigate to="/estoque/saldo" replace />} />
          <Route path="estoque/saldo" element={<EstoquePage abaInicial="saldo" />} />
          <Route path="estoque/movimentacoes" element={<EstoquePage abaInicial="movimentacoes" />} />
          <Route path="estoque/transferencias" element={<TransferenciasEstoquePage />} />
          <Route path="estoque/entradas" element={<Navigate to="/estoque/movimentacoes" replace />} />
          <Route path="estoque/saidas" element={<Navigate to="/estoque/movimentacoes" replace />} />
          <Route path="estoque/baixo" element={<Navigate to="/estoque/saldo" replace />} />
          <Route path="estoque-baixo" element={<Navigate to="/estoque/saldo" replace />} />
          <Route path="estoque/inventario-ajuste-estoque" element={<InventarioAjusteEstoquePage />} />
          <Route path="estoque/relatorios" element={<RelatoriosHubPage modulo="estoque" />} />

          <Route path="financeiro/visao-geral" element={<Navigate to="/financeiro/fluxo-caixa-diario-mensal" replace />} />
          <Route path="financeiro/plano-contas" element={<PlanoContasPage />} />
          <Route path="financeiro/maquininhas" element={<MaquininhasPage />} />
          <Route path="financeiro/receitas" element={<ReceitasPage />} />
          <Route path="financeiro/previsibilidade" element={<PrevisibilidadePage />} />
          <Route path="financeiro/vendas-parceladas" element={<VendasParceladasPage />} />
          <Route path="financeiro/despesas" element={<DespesasPage />} />
          <Route path="financeiro/comissoes" element={<ComissoesFinanceiroPage />} />
          <Route
            path="financeiro/inventario-ajuste-estoque"
            element={<Navigate to="/estoque/inventario-ajuste-estoque" replace />}
          />
          <Route path="financeiro/caixa" element={<CaixaPage />} />
          <Route path="financeiro/fluxo-caixa-diario-mensal" element={<CaixaPage />} />
          <Route path="financeiro/dre" element={<DrePage />} />
          <Route path="financeiro/dre-gerencial-comercial" element={<DrePage />} />
          <Route path="financeiro/relatorios" element={<RelatoriosHubPage modulo="financeiro" />} />

          <Route path="vendas/relatorios" element={<RelatoriosHubPage modulo="vendas" />} />

          <Route path="usuarios/perfis" element={<PerfisPage />} />
          <Route path="usuarios/logins" element={<LoginsPage />} />

          <Route path="auditoria/log-acesso" element={<LogAcessoPage />} />
          <Route path="plataforma/chat-interno" element={<ChatInternoAdminPage />} />

          {/* Módulos novos — telas ainda em construção; os submenus entram depois. */}
          <Route path="fiscal" element={<RecursoEmpresaRoute recurso="fiscal"><Navigate to="/vendas/monitor-fiscal" replace /></RecursoEmpresaRoute>} />
          <Route path="checklists" element={<EmConstrucao titulo="Checklists Inteligentes" />} />
          <Route path="workflow" element={<EmConstrucao titulo="Workflow Inteligente" />} />
          <Route path="agenda-digital" element={<EmConstrucao titulo="Agenda Digital" />} />
          <Route path="caixa-sugestao" element={<EmConstrucao titulo="Caixa de Sugestão" />} />
          <Route path="pesquisa-satisfacao" element={<EmConstrucao titulo="Pesquisa de Satisfação" />} />
          <Route path="ajuda" element={<EmConstrucao titulo="Ajuda" />} />

          <Route path="documentos-ia" element={<EmConstrucao titulo="Documentos com IA" />} />

          <Route path="bloco-notas" element={<BlocoNotasPage />} />
          <Route path="protocolo" element={<ProtocoloPage />} />
          <Route
            path="configuracoes"
            element={
              <RotaDonoPlataforma>
                <ConfiguracoesPage />
              </RotaDonoPlataforma>
            }
          />

          <Route path="*" element={<RotaNaoEncontrada />} />
        </Route>
        </Routes>
      </Suspense>
    </>
  );
}
